"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { SearchResults } from "@/lib/search";
import { EMPTY_RESULTS, toFlatItems } from "./search";

export function useGlobalSearch() {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // true = the floating Alt+K command palette; false = the inline dropdown
  // attached under the navbar's own input. See the file-level comment.
  const [paletteMode, setPaletteMode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const paletteInputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [shortcutLabel, setShortcutLabel] = useState("Alt K");
  useEffect(() => {
    if (!navigator.platform?.toLowerCase().includes("mac")) return;
    const id = setTimeout(() => setShortcutLabel("⌥K"), 0);
    return () => clearTimeout(id);
  }, []);

  const flatItems = useMemo(() => toFlatItems(results), [results]);
  const hasQuery = query.trim().length > 0;
  const hasAnyResults =
    results.articles.length > 0 ||
    results.applications.length > 0 ||
    results.clients.length > 0 ||
    results.supportTickets.length > 0 ||
    results.feedback.length > 0 ||
    results.users.length > 0;
  const showResults = searchOpen && hasQuery;

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const timer = setTimeout(() => {
      setSearchLoading(true);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      fetch(`/api/search?q=${encodeURIComponent(trimmed)}`, { signal: controller.signal })
        .then((res) => (res.ok ? (res.json() as Promise<SearchResults>) : EMPTY_RESULTS))
        .then((data) => {
          setResults(data);
          setActiveIndex(-1);
        })
        .catch((err) => {
          if (err?.name !== "AbortError") setResults(EMPTY_RESULTS);
        })
        .finally(() => setSearchLoading(false));
    }, 180);

    return () => clearTimeout(timer);
  }, [query]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setPaletteMode(false);
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(target) &&
        (!panelRef.current || !panelRef.current.contains(target))
      ) {
        closeSearch();
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [searchOpen, closeSearch]);

  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteMode(true);
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (paletteMode && searchOpen) paletteInputRef.current?.focus();
  }, [paletteMode, searchOpen]);

  const navigateTo = useCallback(
    (href: string) => {
      closeSearch();
      searchInputRef.current?.blur();
      paletteInputRef.current?.blur();
      router.push(href);
    },
    [router, closeSearch],
  );

  function handleQueryChange(value: string) {
    setQuery(value);
    if (!value.trim()) {
      abortRef.current?.abort();
      setResults(EMPTY_RESULTS);
      setSearchLoading(false);
      setActiveIndex(-1);
    }
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      closeSearch();
      searchInputRef.current?.blur();
      paletteInputRef.current?.blur();
      return;
    }
    if (!searchOpen || flatItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flatItems.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = activeIndex >= 0 ? flatItems[activeIndex] : flatItems[0];
      if (target) navigateTo(target.href);
    }
  }

  return {
    query, results, searchLoading, searchOpen, setSearchOpen, paletteMode, setPaletteMode, activeIndex,
    searchInputRef, paletteInputRef, searchBoxRef, panelRef, shortcutLabel, flatItems, hasAnyResults,
    showResults, closeSearch, navigateTo, handleQueryChange, handleSearchKeyDown,
  };
}

export type GlobalSearch = ReturnType<typeof useGlobalSearch>;
